// react
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { XCircle } from "lucide-react";

// utils
import Grid from "../Grid";
import getSizeClass from "./utils";
import Spacer from "../Spacer";

// styles
import css from "./Modal";

const Modal = (props) => {
	const { buttons, content, handleClose, size, showCloseButton } = props;

	// get the class for the size of the modal
	const sizeClass = getSizeClass(size);

	// create the root that we will mount the modal to using a portal
	const modalRoot = useRef(document.createElement("div"));
	const contentRef = useRef(null);
	const hasAutoFocused = useRef(false);

	// On mount we want to add a curtain backdrop
	// so users can focus on the modal
	// On dismount we want to remove the curtain backdrop
	useEffect(() => {
		modalRoot.current.classList.add(css.curtainBackdrop);
		document.body.appendChild(modalRoot.current);

		// Add event listener for Esc key
		const handleEscKey = (event) => {
			if (event.key === "Escape" && showCloseButton) {
				handleClose();
			}
		};

		document.addEventListener("keydown", handleEscKey);

		// Autofocus on the first focusable element - only once when modal first mounts
		if (contentRef.current && !hasAutoFocused.current) {
			const focusableElements = contentRef.current.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			);
			if (focusableElements.length > 0) {
				focusableElements[0].focus();
				hasAutoFocused.current = true;
			}
		}

		return () => {
			modalRoot.current.remove();
			document.removeEventListener("keydown", handleEscKey);
		};
	}, [handleClose, showCloseButton]);

	const handleKeyDown = (event) => {
		if (event.key === "Enter" && showCloseButton) {
			handleClose();
		}
	};

	/**
	 * Takes in a content react element.
	 * When calling this modal you will need to pass in
	 * a div or any react element which will be rendered
	 * All buttons need to have their own functions passed in to make this modal
	 * as customizable as possible
	 * ConfirmOnly boolean will create only one confirmation button to the modal
	 */
	return createPortal(
		<div testid="reactModal" className={`${css.main} ${css[sizeClass]}`}>
			<Spacer size={1} />

			<div className={css.modalHead}>
				<Grid columns={["1fr", "40px"]}>
					<div />

					{showCloseButton && (
						<div testid="closeWrapper" className={css.closeBoxWrapper}>
							<div
								onClick={handleClose}
								onKeyDown={handleKeyDown}
								className={css.closeBox}
								tabIndex={0}
								role="button"
								aria-label="Close modal"
							>
								<XCircle size={16} />
							</div>
						</div>
					)}
				</Grid>
			</div>

			<div testid="modalContent" className={css.modalContent} ref={contentRef}>
				{content}
			</div>

			<div testid="modalButtons" className={css.modalButtons}>
				{buttons}
			</div>
		</div>,
		modalRoot.current
	);
};

Modal.propTypes = {
	content: PropTypes.any,
	buttons: PropTypes.any,
	handleClose: PropTypes.func.isRequired,
	size: PropTypes.oneOf(["small", "large", "extralarge"]),
	showCloseButton: PropTypes.bool,
};

Modal.defaultProps = {
	content: "",
	size: "small",
	buttons: null,
	showCloseButton: true,
};

export default Modal;
